import { Grid, Typography } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import { QueryClient } from '@tanstack/react-query';
import { getJob } from 'api/jobs';
import { runSecondaryAnalysis } from 'api/platform';
import { SelectionMode } from 'components/atoms/RowSelectionContext';
import { CalculateFeaturesJob, isSecondaryAnalysisJob } from 'interfaces/job';
import { compact, countBy, filter, forEach, includes, isEmpty, join, map, partition, size } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { mapSecondaryAnalysisRebuildPayload } from 'services/secondaryAnalysis/useRunSecondaryAnalysisRebuild';
import { executeInChunks } from 'utils/batching';
import { humanize } from 'utils/helpers';
import { getJobQueryKey } from 'utils/queryHooks/jobs/useJob';
import { jobsQueryKey } from 'utils/queryHooks/jobs/useJobs';

const FETCH_SELECTED_JOBS_CHUNK_SIZE = 50;
const START_REBUILD_CHUNK_SIZE = 25;

/**
 * Lifecycle phases for the bulk rebuild flow.
 *
 * Progression:
 * 1) NOT_STARTED (no active bulk rebuild run)
 * 2) COLLECTING_SELECTED_JOB_IDS (build final selected id list from selection mode)
 * 3) FETCHING_SELECTED_JOBS
 * 4) AWAITING_CONFIRMATION
 * 5) STARTING_REBUILDS
 * 6) SUCCEEDED | FAILED | CANCELLED
 *
 * CANCELLING can be entered from any in-progress phase when cancellation is requested,
 * then transitions to CANCELLED as the final terminal state.
 */
export enum BatchRebuildPhase {
  NOT_STARTED = 'NOT_STARTED',
  COLLECTING_SELECTED_JOB_IDS = 'COLLECTING_SELECTED_JOB_IDS',
  FETCHING_SELECTED_JOBS = 'FETCHING_SELECTED_JOBS',
  AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
  STARTING_REBUILDS = 'STARTING_REBUILDS',
  CANCELLING = 'CANCELLING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface BatchRebuildProgress {
  phase: BatchRebuildPhase;
  selectedCount: number;
  fetchedCount: number;
  eligibleCount: number;
  attemptedStarts: number;
}

const initialBatchRebuildProgress: BatchRebuildProgress = {
  phase: BatchRebuildPhase.NOT_STARTED,
  selectedCount: 0,
  fetchedCount: 0,
  eligibleCount: 0,
  attemptedStarts: 0,
};

interface FilteredJobIdsQuery {
  data?: string[];
  refetch: (options: { throwOnError: boolean }) => Promise<{ data?: string[] }>;
}

interface UseJobsBatchRebuildParams {
  canBatchRebuildJobs: boolean;
  selectionMode: SelectionMode;
  selectedRows: GridRowId[];
  omittedRows: GridRowId[];
  clearSelection: () => void;
  filteredJobIdsQuery: FilteredJobIdsQuery;
  confirmWithModal: (props: any) => Promise<boolean>;
  enqueueSnackbar: (message: string, options: { variant: 'success' | 'warning' | 'error' }) => void;
  queryClient: QueryClient;
}

export const useJobsBatchRebuild = ({
  canBatchRebuildJobs,
  selectionMode,
  selectedRows,
  omittedRows,
  clearSelection,
  filteredJobIdsQuery,
  confirmWithModal,
  enqueueSnackbar,
  queryClient,
}: UseJobsBatchRebuildParams) => {
  const [isPreparingBatchRebuild, setIsPreparingBatchRebuild] = useState(false);
  const [isBatchRebuilding, setIsBatchRebuilding] = useState(false);
  const [isBatchCancellationRequested, setIsBatchCancellationRequested] = useState(false);
  const [batchRebuildProgress, setBatchRebuildProgress] = useState<BatchRebuildProgress>(initialBatchRebuildProgress);
  const cancellationRequestedRef = useRef(false);

  const getSelectedJobIds = async () => {
    if (selectionMode === 'select') {
      return map(selectedRows, (selectedJobId) => String(selectedJobId));
    }

    const filteredJobIds =
      filteredJobIdsQuery.data ??
      (
        await filteredJobIdsQuery.refetch({
          throwOnError: true,
        })
      ).data ??
      [];

    return filter(filteredJobIds, (jobId) => !includes(omittedRows, jobId));
  };

  const batchProgressText = useMemo(() => {
    if (isBatchCancellationRequested || batchRebuildProgress.phase === BatchRebuildPhase.CANCELLING) {
      return 'Cancelling batch rebuild...';
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.COLLECTING_SELECTED_JOB_IDS) {
      return 'Collecting selected job IDs...';
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.FETCHING_SELECTED_JOBS) {
      return `Fetching selected jobs: ${batchRebuildProgress.fetchedCount}/${batchRebuildProgress.selectedCount}`;
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.AWAITING_CONFIRMATION) {
      return `Reviewing eligible jobs: ${batchRebuildProgress.eligibleCount}/${batchRebuildProgress.selectedCount}`;
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.STARTING_REBUILDS) {
      return `Starting rebuilds: ${batchRebuildProgress.attemptedStarts}/${batchRebuildProgress.eligibleCount}`;
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.SUCCEEDED) {
      return 'Batch rebuild completed successfully.';
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.FAILED) {
      return 'Batch rebuild completed with errors.';
    }

    if (batchRebuildProgress.phase === BatchRebuildPhase.CANCELLED) {
      return 'Batch rebuild was cancelled.';
    }

    return '';
  }, [batchRebuildProgress, isBatchCancellationRequested]);

  const handleCancelBatchRebuild = () => {
    if (!isPreparingBatchRebuild && !isBatchRebuilding) {
      return;
    }

    cancellationRequestedRef.current = true;
    setIsBatchCancellationRequested(true);
    setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.CANCELLING }));
  };

  /**
   * Runs the bulk rebuild workflow in explicit phases so progress can be surfaced in the UI.
   *
   * Phase breakdown:
   * - COLLECTING_SELECTED_JOB_IDS: build selected job ids
   *   (direct selection or filtered minus omitted rows)
   * - FETCHING_SELECTED_JOBS: fetch source jobs in chunks and classify eligible/unsupported/failed fetches
   * - AWAITING_CONFIRMATION: present summary and wait for user confirmation
   * - STARTING_REBUILDS: start eligible secondary-analysis rebuilds in chunks
   * - CANCELLING: terminal in-progress state used when user requests cancellation
   */
  const handleBatchRebuild = async () => {
    if (!canBatchRebuildJobs || isPreparingBatchRebuild || isBatchRebuilding) {
      return;
    }

    cancellationRequestedRef.current = false;
    setIsBatchCancellationRequested(false);
    setIsPreparingBatchRebuild(true);
    setBatchRebuildProgress({
      ...initialBatchRebuildProgress,
      phase: BatchRebuildPhase.COLLECTING_SELECTED_JOB_IDS,
    });

    let selectedJobIds: string[] = [];

    try {
      selectedJobIds = await getSelectedJobIds();

      if (isEmpty(selectedJobIds)) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.FAILED }));
        enqueueSnackbar('No selected jobs found for rebuild.', {
          variant: 'warning',
        });
        return;
      }

      if (cancellationRequestedRef.current) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.CANCELLED }));
        enqueueSnackbar('Batch rebuild was canceled before processing started.', {
          variant: 'warning',
        });
        return;
      }

      setBatchRebuildProgress((previousProgress) => ({
        ...previousProgress,
        phase: BatchRebuildPhase.FETCHING_SELECTED_JOBS,
        selectedCount: size(selectedJobIds),
      }));

      setIsPreparingBatchRebuild(false);
      setIsBatchRebuilding(true);

      const selectedJobsResults = await executeInChunks(
        selectedJobIds,
        FETCH_SELECTED_JOBS_CHUNK_SIZE,
        async (selectedJobId) => {
          const selectedJob = await queryClient.fetchQuery<CalculateFeaturesJob>(
            getJobQueryKey(selectedJobId),
            () => getJob<CalculateFeaturesJob>(selectedJobId),
            { retry: false }
          );

          if (!selectedJob) {
            throw new Error('Source job not found');
          }

          return selectedJob;
        },
        {
          shouldStop: () => cancellationRequestedRef.current,
          onChunkComplete: (processedCount) => {
            setBatchRebuildProgress((previousProgress) => ({
              ...previousProgress,
              phase: cancellationRequestedRef.current
                ? BatchRebuildPhase.CANCELLING
                : BatchRebuildPhase.FETCHING_SELECTED_JOBS,
              selectedCount: size(selectedJobIds),
              fetchedCount: processedCount,
            }));
          },
        }
      );

      const selectedJobs = compact(
        map(selectedJobsResults, (result) => (result.status === 'fulfilled' ? result.value : null))
      );
      const failedToFetchJobIds = compact(
        map(selectedJobsResults, (result, index) => (result.status === 'rejected' ? selectedJobIds[index] : null))
      );

      const eligibleSecondaryAnalysisJobs = filter(selectedJobs, (job) =>
        isSecondaryAnalysisJob(job)
      ) as CalculateFeaturesJob[];
      const unsupportedSelectedJobs = filter(selectedJobs, (job) => !isSecondaryAnalysisJob(job));

      if (cancellationRequestedRef.current) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.CANCELLED }));
        const skippedSelectedJobIdsCount = Math.max(size(selectedJobIds) - size(selectedJobsResults), 0);

        enqueueSnackbar(
          `Batch rebuild canceled during preparation. Processed ${size(
            selectedJobsResults
          )} selected job(s), skipped ${skippedSelectedJobIdsCount} pending job(s), and found ${size(
            eligibleSecondaryAnalysisJobs
          )} eligible Secondary Analysis job(s).`,
          {
            variant: 'warning',
          }
        );

        return;
      }

      setBatchRebuildProgress((previousProgress) => ({
        ...previousProgress,
        phase: BatchRebuildPhase.AWAITING_CONFIRMATION,
        fetchedCount: size(selectedJobsResults),
        eligibleCount: size(eligibleSecondaryAnalysisJobs),
      }));

      const unsupportedJobTypesSummary = join(
        map(countBy(unsupportedSelectedJobs, 'type'), (count, jobType) => `${humanize(jobType)} (${count})`),
        ', '
      );

      const confirmed = await confirmWithModal({
        title: 'Confirm Bulk Rebuild',
        text: `You are about to run bulk rebuild for ${size(selectedJobIds)} selected job(s).`,
        content: (
          <Grid container direction="column" spacing={1} mt={0.5}>
            <Grid item>
              <Typography variant="body2">
                Will run now: {size(eligibleSecondaryAnalysisJobs)} Secondary Analysis job(s).
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">
                Not supported yet and skipped: {size(unsupportedSelectedJobs)} job(s)
                {unsupportedJobTypesSummary ? ` (${unsupportedJobTypesSummary})` : ''}.
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">Failed to fetch and skipped: {size(failedToFetchJobIds)} job(s).</Typography>
            </Grid>
          </Grid>
        ),
        confirmButtonProps: { title: 'Rebuild' },
        cancelButtonProps: { title: 'Cancel' },
      });

      if (!confirmed) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.CANCELLED }));
        return;
      }

      if (isEmpty(eligibleSecondaryAnalysisJobs)) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.FAILED }));
        enqueueSnackbar(
          `No eligible Secondary Analysis jobs found in the current selection. Skipped ${size(
            failedToFetchJobIds
          )} failed fetches and ${size(unsupportedSelectedJobs)} unsupported job(s).`,
          {
            variant: 'warning',
          }
        );
        return;
      }

      const rebuildJobIds = map(eligibleSecondaryAnalysisJobs, 'id');
      setBatchRebuildProgress((previousProgress) => ({
        ...previousProgress,
        phase: BatchRebuildPhase.STARTING_REBUILDS,
      }));

      const rebuildResults = await executeInChunks(
        eligibleSecondaryAnalysisJobs,
        START_REBUILD_CHUNK_SIZE,
        async (sourceJob) => {
          const selectedJobId = sourceJob.id;
          const rebuildPayload = mapSecondaryAnalysisRebuildPayload({
            sourceJob: {
              studyId: sourceJob?.studyId,
              primaryRunOrchestrationId: sourceJob?.primaryRunOrchestrationId,
              params: sourceJob?.params,
              manifest: sourceJob?.manifest,
            },
            jobName: sourceJob?.name || `Secondary Analysis Rebuild (${selectedJobId})`,
            jobDescription: sourceJob?.description || '',
          });

          await runSecondaryAnalysis(rebuildPayload);
        },
        {
          shouldStop: () => cancellationRequestedRef.current,
          onChunkComplete: (processedCount) => {
            setBatchRebuildProgress((previousProgress) => ({
              ...previousProgress,
              phase: cancellationRequestedRef.current
                ? BatchRebuildPhase.CANCELLING
                : BatchRebuildPhase.STARTING_REBUILDS,
              eligibleCount: size(eligibleSecondaryAnalysisJobs),
              attemptedStarts: processedCount,
            }));
          },
        }
      );

      const [successfulRebuilds, failedRebuilds] = partition(rebuildResults, (result) => result.status === 'fulfilled');

      forEach(rebuildResults, (result, index) => {
        const jobId = rebuildJobIds[index];
        if (result.status === 'rejected') {
          console.error(`Failed to start secondary analysis rebuild for job ${jobId}:`, result.reason);
        }
      });

      const successCount = size(successfulRebuilds);
      const failureCount = size(failedRebuilds);
      const canceledPendingRebuildsCount = Math.max(size(eligibleSecondaryAnalysisJobs) - size(rebuildResults), 0);
      const canceledPendingFetchesCount = Math.max(size(selectedJobIds) - size(selectedJobsResults), 0);

      if (cancellationRequestedRef.current) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.CANCELLED }));
        enqueueSnackbar(
          `Canceled batch rebuild. Started ${successCount} rebuild job(s), failed to fetch ${size(
            failedToFetchJobIds
          )} job(s), failed to start ${failureCount} job(s), and skipped ${
            canceledPendingFetchesCount + canceledPendingRebuildsCount
          } pending job(s).`,
          {
            variant: 'warning',
          }
        );
      } else if (successCount > 0 && failureCount === 0 && isEmpty(failedToFetchJobIds)) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.SUCCEEDED }));
        enqueueSnackbar(`Started ${successCount} secondary analysis rebuild job(s).`, {
          variant: 'success',
        });
      } else if (successCount > 0) {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.FAILED }));
        const failedRebuildJobIds = compact(
          map(rebuildResults, (result, index) => (result.status === 'rejected' ? rebuildJobIds[index] : null))
        );
        enqueueSnackbar(
          `Started ${successCount} secondary analysis rebuild job(s). Failed to fetch ${size(
            failedToFetchJobIds
          )} job(s) and failed to start ${failureCount} job(s)${
            isEmpty(failedRebuildJobIds) ? '.' : ` with ids: ${join(failedRebuildJobIds, ', ')}.`
          }`,
          { variant: 'warning' }
        );
      } else {
        setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.FAILED }));
        enqueueSnackbar('Failed to start selected secondary analysis rebuild jobs.', {
          variant: 'error',
        });
      }

      queryClient.invalidateQueries([jobsQueryKey]);

      if (successCount > 0) {
        clearSelection();
      }
    } catch (error) {
      setBatchRebuildProgress((previousProgress) => ({ ...previousProgress, phase: BatchRebuildPhase.FAILED }));
      console.error('Unexpected error during bulk rebuild:', error);
      enqueueSnackbar('Unexpected error while running bulk rebuild.', {
        variant: 'error',
      });
    } finally {
      setIsBatchRebuilding(false);
      setIsPreparingBatchRebuild(false);
      setIsBatchCancellationRequested(false);
      cancellationRequestedRef.current = false;
    }
  };

  return {
    isPreparingBatchRebuild,
    isBatchRebuilding,
    isBatchCancellationRequested,
    batchProgressText,
    handleBatchRebuild,
    handleCancelBatchRebuild,
    batchRebuildProgress,
  };
};

export default useJobsBatchRebuild;
